import axios from 'axios';
import Vue from 'vue';
import VueCookies from 'vue-cookies';
import { URL_TOKEN_REFRESH } from './urls';
import store from "~/store/store";
import router from "~/router";
import { baseUrl, baseUrlApi  } from '~/variables';
import { handleGlobalErrors } from './errors';
import { notifyGlobalError } from './notify';

Vue.use(VueCookies);

const jwtDecode = require('jwt-decode');

const serverError = 'Le serveur est temporairement inaccessible. Veuillez réessayer ultérieurement.';

const vueTradingClient = axios.create({
  baseURL: baseUrl,
  // Reject only if the status code is greater than or equal to 500
  validateStatus: status => status < 500
});
vueTradingClient.defaults.headers.common['Accept-Language'] = 'fr';

/**
 * It sets the jwt token in axios headers and in localstorage
 * @param {String} jwt
 */
export function setJWT(jwt) {
  if (jwt !== null) {
    vueTradingClient.defaults.headers.common.Authorization = `Bearer ${jwt}`;
  }
}

export function removeJwt() {
  vueTradingClient.defaults.headers.common.Authorization = undefined;
}

function isGlobalAPIError(error) {
  return error.response && error.response.data && error.response.data.errors
}

export function disconnectUser() {
  store.commit('user/unsetUser');
  router.replace({ name: 'login' })
}

/**
 * Refreshes the user jwt access token or disconnects
 * the user if the refresh token is out expired
 */
async function refreshToken() {
  const payload = { refresh: store.state.user.refreshToken };
  // eslint-disable-next-line no-use-before-define
  removeJwt();
  await apiPost(URL_TOKEN_REFRESH, payload, false).then((response) => {
    store.commit('user/setAccessToken', response.data.access);
    store.commit('user/setRefreshToken', response.data.refresh);
    setJWT(response.data.access);
  }).catch((error) => {
    disconnectUser();
  });
}

/**
 * It verifies if the jwt access token is not expired
 * Otherwise, it refreshes it
 */
async function verifyToken() {
  if (vueTradingClient.defaults.headers.common.Authorization === undefined) {
    setJWT(store.state.user.accessToken)
  }
  const token = store.state.user.accessToken;
  if(!token) {
    disconnectUser()
  }
  const decodedToken = jwtDecode(token);
  const date = Math.round(new Date() / 1000);
  if (decodedToken.exp <= date) {
    await refreshToken();
  }
}

/**
 * Wrapper to call get urls with axios http client
 * @param {String} url
 * @param {Boolean} verify
 */
export async function apiGet(url, verify = true) {
  if (verify) {
    await verifyToken();
  }
  return new Promise((resolve, reject) => {
    vueTradingClient.get(baseUrlApi.concat(url))
      .then((response) => {
        resolve(response);
      }).catch((error) => {
        if (isGlobalAPIError(error)) {
          // API system error
          handleGlobalErrors(error.response);
        } else {
          // SGI server not started
          notifyGlobalError(serverError);
        }
        reject(error);
      });
  });
}

/**
 * Wrapper to call post urls with axios http client
 * @param {String} url
 * @param {Object} payload
 * @param {Boolean} verify
 */
export async function apiPost(url, payload, verify = true) {
  if (verify) {
    await verifyToken();
  }
  return new Promise((resolve, reject) => {
    vueTradingClient.post(baseUrlApi.concat(url), payload)
      .then((response) => {
        resolve(response);
      }).catch((error) => {
        if (isGlobalAPIError(error)) {
          // API system error
          handleGlobalErrors(error.response);
        } else {
          // SGI server not started
          notifyGlobalError(serverError);
        }
        reject(error);
      });
  });
}

/**
 * Wrapper to call put urls with axios http client
 * @param {Strin} url
 * @param {Object} payload
 * @param {Boolean} verify
 */
export async function apiPut(url, payload, verify = true) {
  if (verify) {
    await verifyToken();
  }
  return new Promise((resolve, reject) => {
    vueTradingClient.put(baseUrlApi.concat(url), payload)
      .then((response) => {
        resolve(response);
      }).catch((error) => {
        if (isGlobalAPIError(error)) {
          // API system error
          handleGlobalErrors(error.response);
        } else {
          // SGI server not started
          notifyGlobalError(serverError);
        }
        reject(error);
      });
  });
}

/**
 * Wrapper to call patch urls with axios http client
 * @param {String} url
 * @param {Object} payload
 * @param {Boolean} verify
 */
export async function apiPatch(url, payload, verify = true) {
  if (verify) {
    await verifyToken();
  }
  return new Promise((resolve, reject) => {
    vueTradingClient.patch(baseUrlApi.concat(url), payload)
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        if (isGlobalAPIError(error)) {
          // API system error
          handleGlobalErrors(error.response);
        } else {
          // SGI server not started
          notifyGlobalError(serverError);
        }
        reject(error);
      });
  });
}
/**
 * Wrapper to call patch urls with axios http client
 * @param {String} url
 * @param {Boolean} verify
 */
export async function apiDelete(url, verify = true) {
  if (verify) {
    await verifyToken();
  }
  return new Promise((resolve, reject) => {
    vueTradingClient.delete(baseUrlApi.concat(url))
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        if (isGlobalAPIError(error)) {
          // API system error
          handleGlobalErrors(error.response);
        } else {
          // SGI server not started
          notifyGlobalError(serverError);
        }
        reject(error);
      });
  });
}
